import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { homestayAPI, locationAPI, subLocationAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Home, MapPin, ArrowRight, CheckCircle2, AlertCircle, Building } from "lucide-react";

const AdminDataMigration = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  
  const [homestays, setHomestays] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  
  const [selectedHomestays, setSelectedHomestays] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedSubLocation, setSelectedSubLocation] = useState("");
  
  const [stats, setStats] = useState({ total: 0, assigned: 0, unassigned: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchSubLocations(selectedLocation);
    } else {
      setSubLocations([]);
      setSelectedSubLocation("");
    }
  }, [selectedLocation]);

  const fetchData = async () => {
    try {
      const [homestaysRes, locationsRes] = await Promise.all([
        homestayAPI.getAll(),
        locationAPI.getAll()
      ]);
      
      const allHomestays = homestaysRes.data;
      setHomestays(allHomestays);
      setLocations(locationsRes.data);
      
      // Calculate stats
      const unassigned = allHomestays.filter(h => !h.sub_location_id).length;
      setStats({
        total: allHomestays.length,
        assigned: allHomestays.length - unassigned,
        unassigned
      });
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubLocations = async (locationId) => {
    try {
      const response = await subLocationAPI.getAll({ location_id: locationId });
      setSubLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch sub-locations:", error);
    }
  };

  const unassignedHomestays = homestays.filter(h => !h.sub_location_id);

  const toggleHomestay = (homestayId) => {
    setSelectedHomestays(prev => 
      prev.includes(homestayId) 
        ? prev.filter(id => id !== homestayId)
        : [...prev, homestayId]
    );
  };

  const selectAll = () => {
    if (selectedHomestays.length === unassignedHomestays.length) {
      setSelectedHomestays([]);
    } else {
      setSelectedHomestays(unassignedHomestays.map(h => h.id));
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedSubLocation) {
      toast.error("Please select a village/area");
      return;
    }
    if (selectedHomestays.length === 0) {
      toast.error("Please select at least one homestay");
      return;
    }

    setAssigning(true);
    try {
      // Assign each homestay individually (API expects single assignment)
      let successCount = 0;
      for (const homestayId of selectedHomestays) {
        try {
          await homestayAPI.update(homestayId, { sub_location_id: selectedSubLocation }, token);
          successCount++;
        } catch (err) {
          console.error(`Failed to assign homestay ${homestayId}:`, err);
        }
      }
      
      toast.success(`Successfully assigned ${successCount} homestays`);
      setSelectedHomestays([]);
      setSelectedLocation("");
      setSelectedSubLocation("");
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign homestays");
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-data-migration">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">
            Data Migration
          </h1>
          <p className="text-muted-foreground">Assign existing homestays to villages and locations</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Home className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Homestays</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">{stats.assigned}</p>
                <p className="text-sm text-muted-foreground">Assigned to Village</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">{stats.unassigned}</p>
                <p className="text-sm text-muted-foreground">Needs Assignment</p>
              </div>
            </div>
          </div>
        </div>

        {unassignedHomestays.length > 0 ? (
          <>
            {/* Assignment Panel */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h2 className="text-lg font-semibold text-foreground mb-4">Bulk Assignment</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Location</label>
                  <Select value={selectedLocation || "none"} onValueChange={(v) => setSelectedLocation(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="select-location">
                      <SelectValue placeholder="Choose location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Choose location</SelectItem>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}, {loc.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Village/Area</label>
                  <Select 
                    value={selectedSubLocation || "none"} 
                    onValueChange={(v) => setSelectedSubLocation(v === "none" ? "" : v)}
                    disabled={!selectedLocation}
                  >
                    <SelectTrigger data-testid="select-village">
                      <SelectValue placeholder={selectedLocation ? "Choose village" : "Select location first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Choose village</SelectItem>
                      {subLocations.map((sl) => (
                        <SelectItem key={sl.id} value={sl.id}>
                          {sl.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={handleBulkAssign} 
                  disabled={assigning || !selectedSubLocation || selectedHomestays.length === 0}
                  className="rounded-full"
                  data-testid="assign-btn"
                >
                  {assigning ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Assign {selectedHomestays.length} Homestays
                </Button>
              </div>
            </div>

            {/* Unassigned Homestays List */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">
                  Unassigned Homestays ({unassignedHomestays.length})
                </h2>
                <Button variant="outline" size="sm" onClick={selectAll}>
                  {selectedHomestays.length === unassignedHomestays.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {unassignedHomestays.map((homestay) => (
                  <div
                    key={homestay.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                      selectedHomestays.includes(homestay.id) 
                        ? "bg-primary/5 border-primary" 
                        : "bg-gray-50 border-transparent hover:bg-gray-100"
                    }`}
                    onClick={() => toggleHomestay(homestay.id)}
                    data-testid={`homestay-item-${homestay.id}`}
                  >
                    <Checkbox 
                      checked={selectedHomestays.includes(homestay.id)}
                      onCheckedChange={() => toggleHomestay(homestay.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground truncate">{homestay.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {homestay.address || "No address"}
                        {homestay.location_name && (
                          <span className="text-primary"> • {homestay.location_name}</span>
                        )}
                      </p>
                    </div>
                    {homestay.location_name && !homestay.sub_location_name && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                        Has Location
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">All Caught Up!</h3>
            <p className="text-muted-foreground">
              All homestays have been assigned to villages. Great job!
            </p>
          </div>
        )}

        {/* Already Assigned Section */}
        {stats.assigned > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-soft">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Already Assigned ({stats.assigned})
            </h2>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {homestays.filter(h => h.sub_location_id).map((homestay) => (
                <div
                  key={homestay.id}
                  className="flex items-center gap-4 p-4 rounded-lg bg-green-50"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground truncate">{homestay.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {homestay.sub_location_name}
                      {homestay.location_name && `, ${homestay.location_name}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDataMigration;
